import { ThreadComposerLayout } from "./ThreadComposerLayout";
import { useState, type ComponentProps } from "react";
import { useComposerHandleContext } from "../../composerHandleContext";
import { makeWorkspaceFileDropHandlers } from "./workspaceFileDrop";
import { ChatActiveThreadHeader } from "./ChatActiveThreadHeader";
import { ChatComposerDock } from "./ChatComposerDock";
import { ChatComposerPrepRows } from "./ChatComposerPrepRows";
import { ChatThreadOverlays } from "./ChatThreadOverlays";
import { ChatTimelinePanel } from "./ChatTimelinePanel";
import { ProviderStatusBanner } from "./ProviderStatusBanner";
import { RateLimitBanner } from "./RateLimitBanner";
import { isProviderRateOrQuotaError } from "@cmmd-forge/shared/publicProviderCopy";
import { ThreadErrorBanner } from "./ThreadErrorBanner";

export interface ChatLoadedThreadSurfaceProps {
  readonly anchorKind: string;
  readonly anchorCmmdId: string | null;
  readonly providerStatus: ComponentProps<typeof ProviderStatusBanner>["status"];
  readonly rateLimitBannerProps: ComponentProps<typeof RateLimitBanner>;
  readonly threadError: string | null;
  readonly onDismissThreadError: () => void;
  readonly headerProps: ComponentProps<typeof ChatActiveThreadHeader>;
  readonly timelinePanelProps: ComponentProps<typeof ChatTimelinePanel>;
  readonly prepRowsProps: ComponentProps<typeof ChatComposerPrepRows>;
  readonly composerDockProps: ComponentProps<typeof ChatComposerDock>;
  readonly overlayProps: ComponentProps<typeof ChatThreadOverlays>;
}

export function ChatLoadedThreadSurface(props: ChatLoadedThreadSurfaceProps) {
  // The whole thread is the drop target, matching t3code. Dropping a screenshot
  // onto the transcript is the natural gesture; requiring the ~100px composer
  // box turned a routine action into an aiming exercise.
  const composerHandleRef = useComposerHandleContext();
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const fileDropHandlers = makeWorkspaceFileDropHandlers({
    setDragActive: setIsFileDragActive,
    addFiles: (files) => composerHandleRef?.current?.addDroppedFiles(files),
  });

  return (
    <div
      className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background"
      data-anchor-kind={props.anchorKind}
      data-thread-file-drag-active={isFileDragActive ? "true" : undefined}
      onDragEnter={fileDropHandlers.onDragEnter}
      onDragOver={fileDropHandlers.onDragOver}
      onDragLeave={fileDropHandlers.onDragLeave}
      onDrop={fileDropHandlers.onDrop}
      {...(props.anchorCmmdId ? { "data-anchor-cmmd-id": props.anchorCmmdId } : {})}
    >
      {/* Non-interactive and above everything: the drop is handled by the
          surface, so this only has to say "let go here". Inset from the edges so it
          reads as an overlay on the thread rather than a new panel. */}
      {isFileDragActive ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-2 z-50 flex items-center justify-center rounded-xl border-2 border-primary/60 border-dashed bg-background/70 font-medium text-sm text-foreground/80"
        >
          Drop files to attach
        </div>
      ) : null}
      <ChatActiveThreadHeader {...props.headerProps} />
      <ProviderStatusBanner status={props.providerStatus} />
      <ThreadErrorBanner
        error={
          (props.rateLimitBannerProps.modelSelection.provider === "codex" ||
            props.rateLimitBannerProps.modelSelection.provider === "claudeAgent") &&
          isProviderRateOrQuotaError(props.threadError)
            ? null
            : props.threadError
        }
        onDismiss={props.onDismissThreadError}
      />
      <ThreadComposerLayout
        key={props.timelinePanelProps.timelineKey}
        composer={
          <>
            <RateLimitBanner {...props.rateLimitBannerProps} />
            <ChatComposerPrepRows {...props.prepRowsProps} />
            <ChatComposerDock {...props.composerDockProps} />
          </>
        }
      >
        <ChatTimelinePanel {...props.timelinePanelProps} />
      </ThreadComposerLayout>
      <ChatThreadOverlays {...props.overlayProps} />
    </div>
  );
}
